import { useEffect, useState } from 'react';
import {
  Box,
  HStack,
  Spinner,
  Text,
  VStack,
  useMediaQuery,
  Pressable,
  Avatar
} from 'native-base';
import PartnerDetails from '../../../../containers/partnerDetails';
import { useSelector } from 'react-redux';
import {
  getTravelPartnersAPI,
  deleteTravelPartnerAPI,
  editTravelPartnerAPI
} from '../../../../api/apis/quicklinks';
import { Toaster } from '../../../../components/toaster';
import AccordionDetail from '../../../../components/accordion';
import { COLORS } from '../../../../utils/colors';
import ContainerCards from '../../../../components/cards';
import FormHeader from '../../../../components/formHeader';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../../../utils/fonts';
import Svg from 'react-native-svg';
import { deleteCardsIconSVG, editIconDetailsPage } from '../../../../utils/svg';
import EventPartnerModal from '../createPartnerModal';
import DividerLine from '../../../../components/divider';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import {
  partnerCallSVG,
  partnerMailSVG,
  partnerNameSVG
} from '../../../../utils/svg';
import CommonAvatar from '../../../../components/avatar';
import CustomModal from '../../../../components/modal';
import MobileModelSheet from '../../../../components/actionSheet';
import CustomButton from '../../../../components/button';
import { LoaderAnimation } from '../../../../components/spinner';
import { hasPermission } from '../../../../utils/permission';
import NoDataFound from '../../../../components/noDataFound';
import ViewUploadImageCards from '../../../../containers/viewUploadImageCards';

const TravelPartners = () => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH
  });
  const { activeTab } = useSelector((state) => {
    return state.event;
  });
  const [partners, setPartners] = useState([]);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPartnerIndex, setSelectedPartnerIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editPartnerData, setEditPartnerData] = useState(null);

  useEffect(() => {
    getTravelPartners();
  }, [activeTab]);

  const getTravelPartners = async () => {
    setIsLoading(true);
    if (activeTab !== 'packages' && activeTab !== 'meet') {
      let res = await getTravelPartnersAPI(activeTab);
      if (res.error) {
        toaster(res.message, 'error', 'error');
        return;
      } else {
        let travelPart = [];
        res.data.data.data?.map((item) => {
          let data = {
            _id: item._id,
            agency: item.agency,
            gst_number: item.gst_number || '',
            logo: item.logo || null,
            contactDetails: item.contactDetails.map((contact) => ({
              contactName: contact.contactName,
              contact_number: contact.contact_number,
              emailId: contact.emailId, // Handle both cases
              is_quotation_submit: contact.is_quotation_submit || false
            }))
          };
          travelPart.push(data);
        });
        setPartners(travelPart);
        setIsOpen(false);
      }
    }
    setIsLoading(false);
  };

  // Add scrollToTop here as well
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleEditClick = (index) => {
    scrollToTop();
    const partnerToEdit = partners[index];
    setEditPartnerData(partnerToEdit);
    setSelectedPartnerIndex(index);
    setShowPartnerModal(true);
  };

  const closePartnerModal = () => {
    setShowPartnerModal(false);
    setEditPartnerData(null);
    setSelectedPartnerIndex(null);
    setIsEditing(false);
  };

  const handleDeleteClick = (index) => {
    setSelectedPartnerIndex(index);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedPartnerIndex(null);
  };

  const handleDeletePartner = async () => {
    if (selectedPartnerIndex === null) return;
    setIsDeleting(true);
    const partnerToDelete = partners[selectedPartnerIndex];
    try {
      LoaderAnimation.show();
      const response = await deleteTravelPartnerAPI(partnerToDelete._id);
      if (response.error) {
        toaster(
          response.message || 'Failed to delete partner',
          'error',
          'error'
        );
      } else {
        const updatedPartners = partners.filter(
          (_, index) => index !== selectedPartnerIndex
        );
        setPartners(updatedPartners);
        toaster(
          response.data.data.message || 'Partner deleted successfully',
          'success',
          'success'
        );
        closeDeleteModal();
      }
    } catch (error) {
      toaster('An error occurred while deleting the partner', 'error', 'error');
    } finally {
      setIsDeleting(false);
      LoaderAnimation.hide();
    }
  };

  const handleEditPartner = async (updatedData) => {
    if (selectedPartnerIndex === null) return;
    setIsEditing(true);

    try {
      LoaderAnimation.show();
      const currentPartner = partners[selectedPartnerIndex];
      const payload = {
        ...updatedData,
        travelpartner_id: currentPartner._id
      };

      const response = await editTravelPartnerAPI(payload, activeTab);
      if (response.error) {
        toaster(
          response.message || 'Failed to update partner',
          'error',
          'error'
        );
      } else {
        const updatedPartners = [...partners];
        updatedPartners[selectedPartnerIndex] = {
          ...updatedPartners[selectedPartnerIndex],
          agency: updatedData.agency,
          gst_number: updatedData.gst_number || '',
          logo: updatedData.logo || null,
          contactDetails: updatedData.contactDetails.map((coord) => ({
            contactName: coord.contactName,
            contact_number: coord.contact_number,
            emailId: coord.emailId,
            is_quotation_submit: coord.is_quotation_submit || false
          }))
        };
        setPartners(updatedPartners);
        toaster(
          response.data.data.message || 'Partner updated successfully',
          'success',
          'success'
        );
        closePartnerModal();
      }
    } catch (error) {
      toaster('An error occurred while updating the partner', 'error', 'error');
    } finally {
      setIsEditing(false);
      LoaderAnimation.hide();
    }
  };

  const getDeleteModalHeader = () => {
    return <>Delete Partner</>;
  };

  const getDeleteModalBody = () => {
    return (
      <Box
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
        >
          Are you sure you want to delete this partner?
        </Text>
      </Box>
    );
  };

  const getDeleteModalFooter = () => {
    return (
      <HStack
        justifyContent={'end'}
        width={'100%'}
        space={4}
      >
        <CustomButton
          onPress={closeDeleteModal}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={handleDeletePartner}
          text={isDeleting ? 'Deleting...' : 'Delete'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.REJECT_TEXT_COLOR}
          isLoading={isDeleting}
        />
      </HStack>
    );
  };

  const renderContactDetails = (contactDetails) => {
    return contactDetails.map((contact, index) => (
      <VStack
        key={index}
        space={1}
        mb={4}
      >
        <Text
          fontWeight='bold'
          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
        >
          Contact Detail {index + 1}
        </Text>
        <HStack
          space={2}
          alignItems='center'
        >
          <CommonAvatar
            width={'30.022px'}
            height={'30px'}
            bgcolor={COLORS.PARTNERS_CARD_COLOR}
            svg={partnerNameSVG}
            svgwh='24'
            view='0 0 24 24'
          />
          <Text
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
          >
            {contact?.contactName || 'N/A'}
          </Text>
        </HStack>
        <HStack
          space={2}
          alignItems='center'
        >
          <CommonAvatar
            width={'30.022px'}
            height={'30px'}
            bgcolor={COLORS.PARTNERS_CARD_COLOR}
            svg={partnerCallSVG}
            svgwh='19'
            view='0 0 19 19'
          />
          <Text
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
          >
            {contact?.contact_number || 'N/A'}
          </Text>
        </HStack>
        <HStack
          space={2}
          alignItems='center'
        >
          <CommonAvatar
            width={'30.022px'}
            height={'30px'}
            bgcolor={COLORS.PARTNERS_CARD_COLOR}
            svg={partnerMailSVG}
            svgwh='20'
            view='0 0 20 20'
          />
          <Text
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
          >
            {Array.isArray(contact?.emailId) 
              ? contact.emailId[0] || 'N/A'
              : contact?.emailId || 'N/A'}
          </Text>
        </HStack>
        <HStack
          space={2}
          alignItems='center'
          mt={2}
        >
          <Text
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
            color={COLORS.BLACK_COLOR}
          >
            Quotation Submission Access :
          </Text>
          <Text
            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
            color={contact?.is_quotation_submit ? COLORS.APPROVE_TEXT_COLOR : COLORS.REJECT_TEXT_COLOR}
          >
            {contact?.is_quotation_submit ? 'Yes' : 'No'}
          </Text>
        </HStack>
      </VStack>
    ));
  };

  return (
    <>
      {isLoading ? (
        <Box
          flex={1}
          alignItems='center'
          justifyContent='center'
        >
          <Spinner
            color={COLORS.THEME_COLOR}
            size='lg'
          />
        </Box>
      ) : (
        <VStack space={6}>
          {partners?.length > 0 ? (
            partners?.map((item, ind) => (
            <ContainerCards
              key={item._id || ind}
              background={COLORS.WHITE_COLOR}
              width='100%'
              height='auto'
              boxShadow='none'
            >
              <VStack
                space={3}
                width='100%'
                p={5}
                pt={4}
                pb={0}
              >
                <Box
                  flexDirection='row'
                  justifyContent={'space-between'}
                  alignItems='center'
                >
                  <Text
                    fontWeight='600'
                    fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                    color={COLORS.ACCORDION_TITLE_COLOR}
                  >
                    {item.agency}
                  </Text>
                  {hasPermission('TRAVEL_PARTNERS') && (
                  <Box
                    flexDirection='row'
                    justifyContent={'space-between'}
                    alignItems='center'
                    gap='1rem'
                  >
                    <Pressable onPress={() => handleEditClick(ind)}>
                      <Avatar
                        bg={'none'}
                        size={'md'}
                      >
                        {editIconDetailsPage}
                      </Avatar>
                    </Pressable>
                    <Pressable onPress={() => handleDeleteClick(ind)}>
                      <Avatar
                        bg={'none'}
                        size={'md'}
                      >
                        {deleteCardsIconSVG}
                      </Avatar>
                    </Pressable>
                  </Box>
                  )}
                </Box>
                
                {/* GST No and Logo Display */}
                <VStack space={4} alignItems="flex-start">
                  {item.gst_number && (
                  <HStack space={1}>
                    <Text
                      fontWeight='600'
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      color={COLORS.ACCORDION_TITLE_COLOR}
                    >
                      GST No.
                    </Text>
                    <Text
                      fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                    >
                      {item.gst_number}
                    </Text>
                  </HStack>
                  )}
                  {item.logo && (
                  <Box>
                    <Text
                       fontWeight='600'
                       fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                       color={COLORS.ACCORDION_TITLE_COLOR}
                      mb={1}
                    >
                      Logo
                    </Text>
                    <ViewUploadImageCards
                      ind={0}
                      url={item.logo}
                      onDeleteImage={() => {}}
                      permission={false}
                      type="view"
                    />
                  </Box>
                  )}
                </VStack>
              </VStack>
              <VStack
                space={3}
                p={5}
                pt={!item.logo ? 4 : 0}
              >
                {renderContactDetails(item.contactDetails)}
              </VStack>
            </ContainerCards>

            // <AccordionDetail
            //   index={ind}
            //   isOpen={isOpen && ind === openIndex}
            //   setIsOpen={setIsOpen}
            //   openIndex={openIndex}
            //   setOpenIndex={setOpenIndex}
            //   title={item.agency}
            //   content={
            //     <PartnerDetails contactDetails={item.contactDetails} />
            //   }
            //   type="travel"
            // />
            ))
          ) : (
            <NoDataFound dataName="travel partners" />
          )}

          <EventPartnerModal
            isOpen={showPartnerModal}
            onClose={closePartnerModal}
            type={activeTab}
            editData={editPartnerData}
            onSubmit={handleEditPartner}
            isEditing={isEditing}
          />

          {showDeleteModal && !MobileWindow ? (
            <CustomModal
              size=''
              top={'20px'}
              isOpen={showDeleteModal}
              onClose={closeDeleteModal}
              header={getDeleteModalHeader()}
              body={getDeleteModalBody()}
              footer={getDeleteModalFooter()}
              width='fit-content'
              height='fit-content'
              fixedPosition={true}
              fixedTop={180}
            />
          ) : (
            <MobileModelSheet
              isOpen={showDeleteModal}
              onClose={closeDeleteModal}
              header={getDeleteModalHeader()}
              body={getDeleteModalBody()}
              footer={getDeleteModalFooter()}
              maxH={'80vh'}
              position='hidden'
            />
          )}
        </VStack>
      )}
    </>
  );
};

export default TravelPartners;
